// The optional parent link (§7).
//
//   Phase
//    ├─ Deliverable ──┐
//    ├─ Milestone  ───┼─ may own Tasks
//    └─ Task ─────────┘  may instead sit directly in the phase
//
// Two rules drive everything here:
//
//   1. Only a task may have a parent, and only a deliverable or milestone may
//      be one. Deliverables and milestones are peers — nothing parents them.
//   2. A stale link is discarded on read rather than migrated. If a deliverable
//      is reclassified as a task, its children stop resolving immediately; no
//      orphaned "Tasks" section, no card claiming a task as its parent.
//
// `parent` is optional and stays optional. Capturing "call John" into a phase
// must never require choosing a deliverable first.

import { pct } from "./format";
import type { Item } from "./types";

export type ProgressSource = "children" | "checklist";
export interface Progress {
  done: number;
  total: number;
  source: ProgressSource;
}

/**
 * Resolve an item's parent, or undefined if it has none or the link is stale.
 *
 * Reads `parent` and falls back to the pre-v5 `milestone` field, so roll-ups
 * created before the v5 schema keep working without a data migration.
 */
export function parentOf(item: Item, itemsById: Map<string, Item>): Item | undefined {
  if (item.type !== "task") return undefined; // deliverables and milestones are peers
  const id = item.parent || item.milestone;
  if (!id || id === item.id) return undefined;
  const parent = itemsById.get(id);
  if (!parent || parent.type === "task") return undefined; // reclassified — link dropped
  return parent;
}

export interface Hierarchy {
  parentOf: (item: Item) => Item | undefined;
  childrenOf: (item: Item) => Item[];
}

/**
 * Index the parent links once for a set of items, so views don't rescan the
 * list per card. Children are keyed by the *resolved* parent, so stale links
 * never produce a child.
 */
export function buildHierarchy(items: Item[]): Hierarchy {
  const byId = new Map(items.map((i) => [i.id, i]));
  const kids = new Map<string, Item[]>();

  for (const item of items) {
    const parent = parentOf(item, byId);
    if (!parent) continue;
    const list = kids.get(parent.id);
    if (list) list.push(item);
    else kids.set(parent.id, [item]);
  }

  return {
    parentOf: (item) => parentOf(item, byId),
    childrenOf: (item) => kids.get(item.id) ?? [],
  };
}

/**
 * Progress for one item, or null when it should show no progress bar at all.
 *
 * Children win over the checklist: an item that owns tasks is measured by them.
 * A milestone is a date — it happened or it did not — so it never reports
 * progress anywhere.
 */
export function progressOf(
  item: Item,
  children: Item[],
  subCounts?: Map<string, { done: number; total: number }>
): Progress | null {
  if (item.type === "milestone") return null;

  if (children.length > 0) {
    const done = children.filter((c) => c.status === "done").length;
    return { done, total: children.length, source: "children" };
  }

  const sub = subCounts?.get(item.id);
  if (sub && sub.total > 0) return { done: sub.done, total: sub.total, source: "checklist" };

  return null;
}

/**
 * itemId -> progress, for every item that has any. This is the single map the
 * progress bars read; it replaces passing raw checklist counts around, so
 * "children before checklist" and "milestones never" hold on every surface at
 * once rather than being re-decided per view.
 *
 * Items with no progress are absent from the map, so `sub && sub.total > 0`
 * render guards keep working unchanged.
 */
export function progressCounts(
  items: Item[],
  subCounts?: Map<string, { done: number; total: number }>
): Map<string, Progress> {
  const { childrenOf } = buildHierarchy(items);
  const out = new Map<string, Progress>();
  for (const item of items) {
    const p = progressOf(item, childrenOf(item), subCounts);
    if (p) out.set(item.id, p);
  }
  return out;
}

/** Percentage for a resolved progress reading. */
export function progressPct(p: Progress): number {
  return pct(p.done, p.total);
}

/**
 * Todoist subtasks, nested the way Todoist draws them: inside the parent.
 *
 * This is a different link from `parent` above. That one is Project Hub's own
 * deliverable/milestone roll-up; this is Todoist's `parent_id`, mirrored as
 * `todoistParentId` (a Todoist id, not a PocketBase one). Any task can be a
 * Todoist subtask, to any depth.
 *
 * A child nests only when its parent is in `items` — a parent hidden by a
 * filter (completed, or not matching the search) leaves the child standing on
 * its own rather than disappearing — and, given `together`, only when the two
 * would be drawn in the same place (a board grouped by status can put a child
 * in a different column from its parent).
 */
export interface TodoistNesting {
  /** Direct subtasks of an item, in Todoist's order. */
  childrenOf: (item: Item) => Item[];
  /** True when the item is drawn inside its parent rather than on its own. */
  isNested: (item: Item) => boolean;
  /** Every subtask below an item, at any depth. */
  descendantsOf: (item: Item) => Item[];
}

export function todoistNesting(items: Item[], together?: (child: Item, parent: Item) => boolean): TodoistNesting {
  const byTodoistId = new Map<string, Item>();
  for (const i of items) if (i.todoistId) byTodoistId.set(i.todoistId, i);

  const kids = new Map<string, Item[]>();
  const nested = new Set<string>();
  for (const item of items) {
    const parent = item.todoistParentId ? byTodoistId.get(item.todoistParentId) : undefined;
    if (!parent || parent.id === item.id || (together && !together(item, parent))) continue;
    nested.add(item.id);
    const list = kids.get(parent.id);
    if (list) list.push(item);
    else kids.set(parent.id, [item]);
  }
  for (const list of kids.values()) list.sort((a, b) => (a.position ?? 0) - (b.position ?? 0));

  const descendantsOf = (item: Item, seen = new Set<string>()): Item[] =>
    (kids.get(item.id) ?? []).flatMap((c) => {
      if (seen.has(c.id)) return [];
      seen.add(c.id);
      return [c, ...descendantsOf(c, seen)];
    });

  return {
    childrenOf: (item) => kids.get(item.id) ?? [],
    isNested: (item) => nested.has(item.id),
    descendantsOf: (item) => descendantsOf(item),
  };
}

/**
 * A list with Todoist subtasks folded into their parents: what to draw at the
 * top level, in the list's own order, and how to find each one's subtasks.
 *
 * Nesting only happens inside `shown`, so a subtask lands under its parent
 * only when both are in the same list (the same day, the same phase). One
 * whose parent is elsewhere stays on its own, and `parentOf` names that parent
 * from `all` so the row can say where it belongs.
 */
export interface ListNesting {
  top: Item[];
  childrenOf: (item: Item) => Item[];
  parentOf: (item: Item) => Item | undefined;
}

export function listNesting(shown: Item[], all: Item[] = shown): ListNesting {
  const nest = todoistNesting(shown);
  const byTodoistId = new Map<string, Item>();
  for (const i of all) if (i.todoistId) byTodoistId.set(i.todoistId, i);
  return {
    top: shown.filter((i) => !nest.isNested(i)),
    childrenOf: nest.childrenOf,
    parentOf: (item) => {
      if (nest.isNested(item) || !item.todoistParentId) return undefined;
      const p = byTodoistId.get(item.todoistParentId);
      return p && p.id !== item.id ? p : undefined;
    },
  };
}
